// Types
import mixins from "vue-typed-mixins";
const isEditorListInstance = instance => {
  return instance.instance.$options.name === 'EditorList';
};
// Styles
import 'leaflet/dist/leaflet.css';
import './EditorMap.css';

// Mixins
import { WithPartialInject } from '../../utils/withInject';
import EditorBase from '../../mixins/EditorBase';
const editorContainerProviderInject = WithPartialInject('registeredEditors');
import { WithRefs } from '../../utils/withRefs';

// Components
import AdminButton from '../ui/AdminButton';
const components = {
  LMap: undefined,
  LMarker: undefined,
  LTileLayer: undefined,
  LPopup: undefined,
  LTooltip: undefined,
  LIcon: undefined
};
if (process.browser) {
  const {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LTooltip,
    LIcon
    // eslint-disable-next-line @typescript-eslint/no-var-requires
  } = require('vue2-leaflet');
  components.LMap = LMap;
  components.LTileLayer = LTileLayer;
  components.LMarker = LMarker;
  components.LPopup = LPopup;
  components.LTooltip = LTooltip;
  components.LIcon = LIcon;
}
const EditorMap = mixins(editorContainerProviderInject, EditorBase(() => ({
  zoom: 7,
  center: [45, 16]
})), WithRefs()).extend({
  name: 'EditorMap',
  components: {
    ...components
  },
  props: {
    zoomSnap: {
      type: Number,
      default: 0.25
    },
    scrollWheelZoom: {
      type: Boolean,
      default: false
    },
    relatedEditorListName: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      options: {
        zoomSnap: this.zoomSnap,
        scrollWheelZoom: this.scrollWheelZoom
      },
      maxBounds: [[-90, -180], [90, 180]],
      minZoom: 2,
      relatedEditorListData: {
        coordinatesOptionName: '',
        instance: null
      },
      markers: []
    };
  },
  computed: {
    //We need this because our computed value for center is array which is reference type
    computedCenter() {
      if (!this.computedValue.center) return [0, 0];
      return [...this.computedValue.center];
    },
    computedZoom() {
      return this.computedValue.zoom;
    }
  },
  watch: {
    $isInEditMode(newVal) {
      //Refresh each time user closes edit mode
      !newVal && this.attemptReadingRelatedEditorList();
    }
  },
  methods: {
    attemptReadingRelatedEditorList() {
      var _this$registeredEdito;
      this.reset();
      if (!this.relatedEditorListName || !((_this$registeredEdito = this.registeredEditors) !== null && _this$registeredEdito !== void 0 && _this$registeredEdito.length)) return;
      for (const editor of this.registeredEditors) {
        if (editor.name === this.relatedEditorListName && isEditorListInstance(editor) && this.editorListContainsCoordinates(editor) && editor.instance.computedValue) {
          //Don't allow reading from more than 1 related editor list for now
          this.readDataFromEditorList(editor);
          return;
        }
      }
    },
    reset() {
      this.relatedEditorListData.coordinatesOptionName = '';
      this.relatedEditorListData.instance = null;
      this.markers = [];
    },
    editorListContainsCoordinates(editor) {
      for (const option of editor.instance.itemOptions) {
        if (option.type === 'coordinates') {
          this.relatedEditorListData.coordinatesOptionName = option.name;
          return true;
        }
      }
      return false;
    },
    readDataFromEditorList(editor) {
      var _this$relatedEditorLi;
      this.relatedEditorListData.instance = editor.instance;
      (_this$relatedEditorLi = this.relatedEditorListData.instance.computedValue) === null || _this$relatedEditorLi === void 0 ? void 0 : _this$relatedEditorLi.forEach(listItem => {
        //Ignore coordinates that are not fully provided
        if (!this.validateCoordinates(listItem)) return;
        this.addNewMarker(listItem);
      });
    },
    validateCoordinates(listItem) {
      // TODO: Bullshit casting.
      const coordinatesOption = listItem[this.relatedEditorListData.coordinatesOptionName];
      //Empty coordinate will always be empty string
      return (coordinatesOption === null || coordinatesOption === void 0 ? void 0 : coordinatesOption.lat) && (coordinatesOption === null || coordinatesOption === void 0 ? void 0 : coordinatesOption.long);
    },
    addNewMarker(listItem) {
      // TODO: Bullshit casting.
      const coordinatesOption = listItem[this.relatedEditorListData.coordinatesOptionName];
      const parsedLat = parseFloat(coordinatesOption === null || coordinatesOption === void 0 ? void 0 : coordinatesOption.lat);
      const parsedLong = parseFloat(coordinatesOption === null || coordinatesOption === void 0 ? void 0 : coordinatesOption.long);
      //Map coordinates to the type our l-marker expects:[number,number]
      const coordinates = [parsedLat, parsedLong];
      const newMarker = {
        coordinates,
        popupItem: listItem
      };
      this.markers.push(newMarker);
    },
    saveCenter() {
      if (!this.$refs.map) return false;
      const {
        mapObject
      } = this.$refs.map;
      if (!mapObject.getCenter()) {
        this.showErrorToast('core.notifications.centerSaveFail');
        return false;
      }
      this.computedValue = {
        ...this.computedValue,
        center: this.mapCenterCoordinates(mapObject.getCenter())
      };
      this.showSuccessToast('core.notifications.centerSaved');
      return true;
    },
    mapCenterCoordinates(coordinates) {
      return [coordinates.lat, coordinates.lng];
    },
    saveZoom() {
      if (!this.$refs.map) return false;
      const {
        mapObject
      } = this.$refs.map;
      if (!mapObject.getZoom()) {
        this.showErrorToast('core.notifications.zoomSaveFail');
        return false;
      }
      this.computedValue = {
        ...this.computedValue,
        zoom: mapObject.getZoom()
      };
      this.showSuccessToast('core.notifications.zoomSaved');
      return true;
    },
    saveZoomAndCenter() {
      const zoomSaved = this.saveZoom();
      const centerSaved = this.saveCenter();
      if (zoomSaved || centerSaved) this.showInfoToast();
    },
    showSuccessToast(tPath) {
      this.$toast.success(this.$t(tPath));
    },
    showErrorToast(tPath) {
      this.$toast.error(this.$t(tPath));
    },
    showInfoToast() {
      this.$toast.info(this.$t('core.warning.saveChanges'));
    }
  },
  render() {
    const h = arguments[0];
    return h("div", {
      "class": "relative z-10"
    }, [h("client-only", [h("l-map", {
      "ref": "map",
      "attrs": {
        "zoom": this.computedZoom,
        "center": this.computedCenter,
        "min-zoom": this.minZoom,
        "max-bounds": this.maxBounds,
        "options": this.options
      },
      "on": {
        "ready": () => this.attemptReadingRelatedEditorList()
      }
    }, [h("l-tile-layer", {
      "attrs": {
        "url": this.url,
        "attribution": this.attribution
      }
    }), this.markers.map((marker, index) => {
      var _this$$scopedSlots$po, _this$$scopedSlots;
      return h("l-marker", {
        "attrs": {
          "lat-lng": marker.coordinates
        },
        "key": `marker-${index}-lat${marker.coordinates[0]}-long${marker.coordinates[1]}`
      }, [h("l-icon", {
        "attrs": {
          "icon-size": [28, 42],
          "icon-anchor": [14, 42],
          "popup-anchor": [0, -35],
          "icon-url": "/cms-public/icons/custom-marker-icon.png"
        }
      }), this.$scopedSlots.popup && h("l-popup", [(_this$$scopedSlots$po = (_this$$scopedSlots = this.$scopedSlots).popup) === null || _this$$scopedSlots$po === void 0 ? void 0 : _this$$scopedSlots$po.call(_this$$scopedSlots, {
        index,
        item: marker.popupItem
      })])]);
    }), this.$isInEditMode && h("div", {
      "class": "absolute z-20 top-0 right-0 pt-4 pr-4"
    }, [h(AdminButton, {
      "on": {
        "click": () => this.saveZoomAndCenter()
      }
    }, [this.$at('core.action.saveZoomAndCenter')])])])])]);
  }
});
export default EditorMap;